/**
 * Стереть запомненное на телефоне для работы без связи (план дня, листы ПСД, отметки «Взять на вахту»), когда
 * человек вышел, переключил компанию, вошёл в другую компанию (команда портала, демо, «глазами пользователя») или
 * сеанс закончился (ответ 401). Так следующий, кто возьмёт телефон, или та же учётная запись в другой компании не
 * увидит чужое без входа (ПМ-09, МП-13).
 *
 * Очереди неотправленных отчётов, замечаний и приёмок не стираются: они уже разделены по человеку и компании и
 * не уйдут от чужого имени. Код площадки грузится лениво — общий интерфейс от него не тяжелеет.
 */
import type { MeResponse } from '@epc/contracts';
import type { QueryClient } from '@tanstack/react-query';

/** Кэш ответов служебного обработчика (public/sw.js, `epc-site-data-<версия>`) */
const DATA_CACHE_PREFIX = 'epc-site-data-';
/** Выход и смена компании не ждут стирания дольше этого: медленная сеть не должна держать человека */
const WAIT_MS = 3000;

let running: Promise<void> | null = null;

async function dropDataCaches(): Promise<void> {
  // Запасной путь: обработчик может быть ещё не активен или код площадки не загрузился (после выкладки)
  if (typeof caches === 'undefined') return;
  const names = await caches.keys();
  await Promise.all(names.filter((n) => n.startsWith(DATA_CACHE_PREFIX)).map((n) => caches.delete(n)));
}

export function wipeOfflineDataSafe(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (!running) {
    const work = Promise.allSettled([
      import('../pages/site/offline/register-sw').then((m) => m.wipeOfflineData()),
      dropDataCaches(),
    ]).then(() => {});
    // Зависшее стирание не должно навсегда занять место: через WAIT_MS следующий вызов начнёт заново
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, WAIT_MS));
    running = Promise.race([work, timeout]).finally(() => {
      running = null;
    });
  }
  return running;
}

/** Шаги самого входа: здесь 401 — неверный пароль, код или ключ, а не конец сеанса */
const SIGN_IN_STEPS = [
  '/auth/login',
  '/auth/second-factor',
  '/auth/passkeys',
  '/auth/password-reset',
  '/auth/invites',
  '/auth/setup',
];

/**
 * 401 означает «сеанс кончился» для всех адресов, кроме шагов самого входа: там человек ещё не вошёл, и стирать
 * нечего — данные прежнего входа уже стёрты при его выходе.
 */
export function isSessionLost(path: string, status: number): boolean {
  if (status !== 401) return false;
  const p = path.split('?')[0] ?? path;
  return !SIGN_IN_STEPS.some((step) => p === step || p.startsWith(`${step}/`));
}

const identityOf = (me: MeResponse | null | undefined) =>
  me?.user && me.tenant ? `${me.tenant.id}:${me.user.id}` : null;

/**
 * Следить за тем, кто вошёл: если в этой вкладке сменились человек или компания (вход команды портала в компанию,
 * демо и смена роли, «глазами пользователя», окончание этих режимов по времени, вход другого человека), запомненное
 * прежним — стереть. Явные выход и смена компании вызывают стирание сами и ждут его до перехода.
 */
export function watchIdentityForOfflineWipe(qc: QueryClient): () => void {
  let last: string | null = identityOf(qc.getQueryData<MeResponse | null>(['me']));
  return qc.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] !== 'me' || event.type === 'removed') return;
    const next = identityOf(event.query.state.data as MeResponse | null | undefined);
    if (!next) return;
    if (last && next !== last) void wipeOfflineDataSafe();
    last = next;
  });
}
